import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
    ServerIcon,
    SignalIcon,
    CpuChipIcon,
    CircleStackIcon,
    ExclamationTriangleIcon,
    ShieldCheckIcon,
    CommandLineIcon
} from '@heroicons/react/24/outline';

const API_URL = '/api/v1';

export default function HubMonitor() {
    const [telemetry, setTelemetry] = useState({
        latency: '142ms',
        syncStatus: 'SYNCHRONIZED',
        buffer: '0',
        uptime: '99.98%'
    });

    const [complianceProof, setComplianceProof] = useState(null);

    useEffect(() => {
        const fetchStatus = async () => {
            try {
                const res = await axios.get(`${API_URL}/hub/status`);
                setTelemetry(prev => ({
                    ...prev,
                    syncStatus: res.data.hub_connected ? 'SYNCHRONIZED' : 'OFFLINE',
                    buffer: res.data.pending_transfers.toString()
                }));
            } catch (e) {
                console.error(e);
            }
        };
        const fetchCompliance = async () => {
            try {
                const res = await axios.get(`${API_URL}/reporting/compliance/xsd-proof`);
                setComplianceProof(res.data);
            } catch (e) {
                console.error('Compliance proof fetch failed', e);
            }
        };
        fetchStatus();
        fetchCompliance();
    }, []);

    return (
        <div className="space-y-8 animate-fade-in text-gray-800">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-3xl font-black text-gray-800 tracking-tight uppercase">Registry Governance</h1>
                    <p className="text-gray-500 font-bold">AIB Hub Interoperability & EECS v8.0 Compliance Evidence</p>
                </div>
                <div className="flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-50 border border-emerald-100">
                    <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                    <span className="text-xs font-black text-emerald-600 uppercase tracking-widest">LIVE INTERCONNECT</span>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                <div className="bg-white p-6 rounded-sm border border-gray-200 shadow-sm">
                    <SignalIcon className="w-6 h-6 text-blue-500 mb-4" />
                    <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1">Hub Latency</p>
                    <p className="text-2xl font-black text-gray-800">{telemetry.latency}</p>
                </div>
                <div className="bg-white p-6 rounded-sm border border-gray-200 shadow-sm">
                    <ServerIcon className="w-6 h-6 text-emerald-500 mb-4" />
                    <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1">Sync Status</p>
                    <p className="text-2xl font-black text-emerald-600">{telemetry.syncStatus}</p>
                </div>
                <div className="bg-white p-6 rounded-sm border border-gray-200 shadow-sm">
                    <CpuChipIcon className="w-6 h-6 text-indigo-500 mb-4" />
                    <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1">Queue Buffer</p>
                    <p className="text-2xl font-black text-gray-800">{telemetry.buffer} <span className="text-xs text-gray-400 uppercase">txs</span></p>
                </div>
                <div className="bg-white p-6 rounded-sm border border-gray-200 shadow-sm">
                    <CircleStackIcon className="w-6 h-6 text-amber-500 mb-4" />
                    <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1">System Uptime</p>
                    <p className="text-2xl font-black text-gray-800">{telemetry.uptime}</p>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                <div className="lg:col-span-2 space-y-8">
                    <div className="bg-white border border-gray-200 p-8 rounded-sm shadow-sm relative overflow-hidden">
                        <div className="absolute top-0 right-0 w-32 h-32 bg-indigo-50 rounded-full -mr-16 -mt-16 opacity-50"></div>
                        <div className="flex items-center gap-3 mb-8">
                            <CommandLineIcon className="w-6 h-6 text-indigo-600" />
                            <h3 className="text-xl font-black text-gray-800 uppercase tracking-tight">Active XSD Validation Engine</h3>
                        </div>

                        {complianceProof && (
                            <div className="space-y-6">
                                <div className="p-6 bg-gray-900 rounded-sm font-mono text-[11px] text-emerald-400 overflow-x-auto shadow-inner border border-gray-800">
                                    <div className="flex justify-between items-center mb-4 pb-2 border-b border-gray-800">
                                        <span className="text-gray-500 uppercase font-black text-[9px]">EECS Hub Message Proof (XSD v8.0)</span>
                                        <span className="px-2 py-1 bg-emerald-500/10 text-emerald-500 rounded text-[9px] font-black">VALIDATED</span>
                                    </div>
                                    <pre>{complianceProof.test_vector}</pre>
                                </div>
                                <div className="flex items-center gap-3 p-4 bg-emerald-50 border border-emerald-100 rounded-sm">
                                    <ShieldCheckIcon className="w-5 h-5 text-emerald-600" />
                                    <p className="text-xs font-bold text-emerald-800 uppercase tracking-tight">
                                        Compliance Result: {complianceProof.validation_result}
                                    </p>
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="bg-white border border-gray-200 p-8 rounded-sm shadow-sm">
                        <h3 className="text-xl font-black text-gray-800 mb-8 uppercase tracking-tight">Registry Interoperability Logs</h3>
                        <div className="space-y-4 font-mono text-[11px]">
                            <div className="p-4 bg-gray-50 text-gray-500 border-l-4 border-emerald-500">
                                <span className="font-bold">[21:44:01]</span> INFO: Outbound Export Envelope created for DE Registry. (GIAI Validation: OK)
                            </div>
                            <div className="p-4 bg-gray-50 text-gray-500 border-l-4 border-indigo-500">
                                <span className="font-bold">[21:43:55]</span> INFO: Schema Validation for MessageID: MAP-99128... SUCCESS.
                            </div>
                            <div className="p-4 bg-gray-50 text-gray-500 border-l-4 border-blue-500">
                                <span className="font-bold">[21:40:12]</span> DEBUG: AIB Hub keep-alive response received (200 OK).
                            </div>
                        </div>
                    </div>
                </div>

                <div className="space-y-6">
                    <div className="bg-[#155e75] p-8 rounded-sm text-white shadow-xl">
                        <h3 className="text-xl font-black mb-6 uppercase tracking-tight">Technical Compliance</h3>
                        <div className="space-y-6">
                            <div className="flex flex-col gap-1 border-b border-white/10 pb-4">
                                <span className="text-[10px] font-black text-white/50 uppercase tracking-widest">Protocol</span>
                                <span className="text-sm font-bold">EECS Rules v8.0</span>
                            </div>
                            <div className="flex flex-col gap-1 border-b border-white/10 pb-4">
                                <span className="text-[10px] font-black text-white/50 uppercase tracking-widest">AIB Hub Standard</span>
                                <span className="text-sm font-bold">Fact Sheet 04/05/16</span>
                            </div>
                            <div className="flex flex-col gap-1">
                                <span className="text-[10px] font-black text-white/50 uppercase tracking-widest">Hashing Engine</span>
                                <span className="text-sm font-bold font-mono">SHA-256 (EECS compliant)</span>
                            </div>
                        </div>
                        <div className="mt-10 p-4 bg-white/5 rounded-sm border border-white/10">
                            <p className="text-[10px] italic text-white/60 leading-relaxed font-medium">
                                The registry is currently operating in "AIB Interop Mode", enabling
                                seamless cross-border transfers via the central European hub.
                            </p>
                        </div>
                    </div>

                    <div className="p-8 rounded-sm bg-amber-50 border border-amber-100">
                        <div className="flex gap-4">
                            <ExclamationTriangleIcon className="w-8 h-8 text-amber-500 shrink-0" />
                            <div>
                                <p className="text-amber-700 font-bold mb-1 text-sm uppercase">Regulatory Advisory</p>
                                <p className="text-[11px] text-amber-600 leading-relaxed font-medium">
                                    GSE Registry (Italy) has announced schema updates for Annex 6.
                                    The local XSD engine will update automatically on May 15.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
